import {
  DeesElement,
  property,
  html,
  customElement,
  TemplateResult,
  css,
  cssManager,
  unsafeCSS,
} from '@designestate/dees-element';

import * as domtools from '@designestate/dees-domtools';

declare global {
  interface HTMLElementTagNameMap {
    'lele-footer': LeleFooter;
  }
}

@customElement('lele-footer')
export class LeleFooter extends DeesElement {
  public static demo = () => html`<lele-footer></lele-footer>`;

  @property()
  public footerText = `Lossless GmbH since 2014`;

  @property()
  public versionText = `version x.x.x`;

  constructor() {
    super();
  }

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        user-select: none;
      }
      :host([hidden]) {
        display: none;
      }

      .footerMain {
        padding-top: 50px;
        background: ${cssManager.bdTheme('#eeeeeb', '#000000')};
        color: ${cssManager.bdTheme('#333', '#fff')};
        position: relative;
        z-index: 1;
      }

      .footerHeading {
        font-size: 16px;
        margin-bottom: 20px;
        text-align: center;
      }

      .footerHeading::after {
        display: block;
        margin: auto;
        margin-top: 10px;
        border-radius: 6px;
        width: 60px;
        height: 1px;
        border-bottom: 1px solid var(--lelecv-color-accent, #e4002b);
        content: '';
      }

      .content {
        max-width: var(--lelecv-pagewidth, 1200px);
        margin: auto;
      }

      .footerLinks {
        display: grid;
        grid-column-gap: 20px;
        grid-row-gap: 50px;
        grid-template-columns: ${unsafeCSS(domtools.css.cssGridColumns(3, 20))};
      }

      .footerLinkColumn {
        display: block;
        border-bottom: 1px solid
          ${cssManager.bdTheme('rgba(0, 0, 0, 0.3)', 'rgba(255, 255, 255, 0.1)')};
      }

      .footerLinkColumnHeading {
        border-bottom: 1px solid
          ${cssManager.bdTheme('rgba(0, 0, 0, 0.3)', 'rgba(255, 255, 255, 0.1)')};
        padding-bottom: 5px;
        margin-bottom: 5px;
      }

      .footerLinkColumn a {
        display: block;
        padding-top: 5px;
        padding-bottom: 5px;
        text-decoration: none;
        font-weight: 300;
        color: ${cssManager.bdTheme('rgba(0, 0, 0, 0.8)', 'rgba(255, 255, 255, 0.8)')};
      }

      .footerLinkColumn a:hover {
        color: ${cssManager.bdTheme('rgba(0, 0, 0, 1)', 'rgba(255, 255, 255, 1)')};
      }

      .closingRemarks {
        text-align: center;
        font-size: 12px;
        font-weight: 300;
        padding-top: 20px;
        padding-bottom: 25px;
      }

      .logo {
        max-width: 80px;
        margin: auto;
        padding-bottom: 30px;
        filter: ${cssManager.bdTheme('invert(1)', '')};
      }

      ${cssManager.cssForTablet(css`
        .content {
          padding-left: 40px;
          padding-right: 40px;
        }
      `)}

      ${cssManager.cssForPhablet(css`
        .content {
          padding-left: 30px;
          padding-right: 30px;
        }
      `)}


      ${cssManager.cssForPhone(css`
        .footerLinks {
          grid-template-columns: 100%;
        }
        .footerLinkColumnHeading {
          text-align: center;
        }
      `)}
    `,
  ];

  public render(): TemplateResult {
    return html`
      <style></style>
      <div class="footerMain">
        <div class="footerHeading">Info and Additional Links</div>
        <div class="content">
          <div class="footerLinks">
            <div class="footerLinkColumn">
              <div class="footerLinkColumnHeading">Your Lossless Account</div>
              <a href="https://account.lossless.org/subscription">Subscription Management</a>
              <a href="https://account.lossless.org/billing">Billing & Invoicing</a>
              <a href="https://account.lossless.org/support">Support & Customer Care</a>
              <a href="https://account.lossless.org/sla">SLA Emergency Handling</a>
              <a href="https://account.lossless.org/gdpr">GDPR Compliance</a>
            </div>
            <div class="footerLinkColumn">
              <div class="footerLinkColumnHeading">Contact</div>
              <a href="mailto:hello@lossless.com">hello@lossless.com</a>
              <a href="tel:+4942198970760">+49 421 989 707 60</a>
              <a href=""
                >Lossless GmbH<br />Karl-Ferdinand-Braun Str. 5<br />28359 Bremen<br />Germany</a
              >
              <a href="">(or chat with us right now)</a>
            </div>
            <div class="footerLinkColumn">
              <div class="footerLinkColumnHeading">Company & Legal</div>
              <a href="https://jobs.lossless.com">Jobs</a>
              <a href="https://roadmap.lossless.com/roadmap">Roadmap</a>
              <a href="https://lossless.gmbh">Legal Info (Impressum)</a>
              <a href="https://lossless.gmbh/privacy">Privacy Policy</a>
              <a href="">Cookie Choice</a>
            </div>
          </div>
          <div class="closingRemarks">${this.footerText}<br />${this.versionText}</div>
          <div class="logo">
            <a href="https://lossless.com" target="_blank"
              ><img
                src="https://assetbroker.lossless.one/brandfiles/lossless/svg-minimal-bright.svg"
            /></a>
          </div>
        </div>
      </div>
    `;
  }
}
